/*
 * cb-monitor.js — Coolbeds conversion-flow monitor (added 25 Sep 2026)
 *
 * Reports silent failures in the buying flow to Microsoft Clarity (custom events + session
 * tags, so the affected recordings can be filtered) and to GA4 (events, for trends).
 * It never changes page behaviour: every hook is wrapped in try/catch and only observes.
 *
 * Clarity custom events (filter recordings by these under "Smart events"):
 *   cb_img_error        a store image failed to load
 *   cb_img_slow         the main product image (LCP) took > IMG_SLOW_MS
 *   cb_script_error     a key script (Razorpay / theme / bundle) failed to load
 *   cb_atc_fail         /cart/add returned an error (sold out, invalid variant, 5xx, network)
 *   cb_atc_slow         /cart/add took > ATC_SLOW_MS
 *   cb_atc_no_response  Add to Cart tapped but no cart request was ever sent (from 13 Oct 2026:
 *                       taps made while an add is already in progress are no longer counted, and a
 *                       sticky-bar tap is counted once - before, it was also counted as a main tap)
 *   cb_atc_hang         a /cart/add request got no answer within ATC_HANG_MS (added 13 Oct 2026)
 *   cb_atc_soldout_tap  sticky "Add To Cart" tapped while the chosen size/colour is sold out
 *                       (detail = the chosen options; added 13 Oct 2026)
 *   cb_checkout_click   Checkout tapped
 *   cb_checkout_open    Razorpay checkout became visible
 *   cb_checkout_slow    it took > CHECKOUT_SLOW_MS to appear
 *   cb_checkout_no_open it never appeared within CHECKOUT_FAIL_MS
 *   cb_checkout_closed  shopper closed the Razorpay popup (tag cb_checkout_exit_step = last step)
 * Session tags hold the details (image URL, error text, timings, browser type).
 *
 * Product-photo visibility (blank / stuck / exit-while-blank) lives ONLY in cb-gallery-guard.js,
 * which checks the photo actually on screen. cb_img_stuck was retired and cb_exit_img_pending
 * moved there (25 Sep 2026): the old checks here used the first <img> in the HTML, which on
 * colour-set galleries is a hidden photo of another colour, so they fired falsely.
 */
(function () {
  'use strict';
  if (window.__cbMonitor) return;
  window.__cbMonitor = true;

  var IMG_SLOW_MS = 3000;
  var ATC_SLOW_MS = 3000;
  var ATC_NO_REQUEST_MS = 2500;
  var ATC_HANG_MS = 10000;
  var CHECKOUT_SLOW_MS = 3000;
  var CHECKOUT_FAIL_MS = 8000;
  var MAX_PER_EVENT = 5; // cap repeats of one event per page view
  // TRACKING-FIX (13 Oct 2026, Claude): GA4 G-80LLZ17V1J only (was also G-8899R3KRW0, a 2nd
  // property that got only these cb_* events - same data twice, and an extra Google script).
  // Undo: add 'G-8899R3KRW0' back to this list.
  var GA4_IDS = ['G-80LLZ17V1J'];

  var w = window, d = document;
  var now = function () { return Math.round(performance.now()); };
  w.clarity = w.clarity || function () { (w.clarity.q = w.clarity.q || []).push(arguments); };

  var ua = navigator.userAgent || '';
  var browserType = /Instagram/i.test(ua) ? 'instagram_app'
    : /FBAN|FBAV|FB_IAB/i.test(ua) ? 'facebook_app'
    : /\bwv\b|WebView/i.test(ua) ? 'android_webview' : 'browser';
  var isProduct = /\/products\//.test(location.pathname);

  var counts = {};
  function report(name, detail, ms) {
    try {
      counts[name] = (counts[name] || 0) + 1;
      if (counts[name] > MAX_PER_EVENT) return;
      detail = detail == null ? '' : String(detail).slice(0, 250);
      w.clarity('event', name);
      if (detail) w.clarity('set', name, detail);
      if (ms != null) w.clarity('set', name + '_ms', String(ms));
      w.clarity('set', 'cb_browser', browserType);
      if (typeof w.gtag === 'function') {
        w.gtag('event', name, {
          send_to: GA4_IDS,
          cb_detail: detail,
          cb_ms: ms == null ? undefined : ms,
          cb_browser: browserType,
          cb_page: location.pathname.slice(0, 100)
        });
      }
    } catch (e) { /* never break the page */ }
  }
  function shortUrl(u) {
    try { var x = new URL(u, location.href); return x.pathname.split('/').pop().slice(0, 120); }
    catch (e) { return String(u).slice(0, 120); }
  }

  /* ---------- 1. Images & key scripts failing to load ---------- */
  var KEY_SCRIPT = /razorpay|magic-shopify|theme\.js|pf-bundle|custom\.js|vendor-scripts/i;
  function isStoreImage(src) { return /coolbeds\.in\/cdn\/|cdn\.shopify\.com/.test(src || ''); }
  function imgArea(el) {
    if (el.closest('.product__main-photos, [data-product-photos]')) return 'product_main';
    if (el.closest('.product__thumbs, .product__thumb')) return 'product_thumb';
    if (el.closest('.grid-product, .grid__item')) return 'grid';
    return 'other';
  }
  function onResourceError(tag, src, el) {
    if (tag === 'IMG' && isStoreImage(src)) {
      report('cb_img_error', (el ? imgArea(el) : '?') + ' ' + shortUrl(src));
    } else if (tag === 'SCRIPT' && KEY_SCRIPT.test(src || '')) {
      report('cb_script_error', shortUrl(src));
    }
  }
  // errors caught by the inline bootstrap in <head> before this file loaded
  (w.__cbErr || []).forEach(function (e) { onResourceError(e[0], e[1], e[3]); });
  w.__cbErr = { push: function (e) { onResourceError(e[0], e[1], e[3]); } };
  w.addEventListener('error', function (e) {
    var t = e.target;
    if (!t || t === w || !t.tagName) return;
    if (t.__cbSeen) return; t.__cbSeen = true;
    onResourceError(t.tagName, t.currentSrc || t.src, t);
  }, true);

  /* ---------- 1b. Main product image speed (LCP only; visibility is in cb-gallery-guard.js) ---------- */
  if (isProduct) {
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries(), last = entries[entries.length - 1];
        if (!last || !last.element || last.element.tagName !== 'IMG') return;
        var t = Math.round(last.startTime);
        // LCP is recorded when the tab is visible; ignore pages opened in a background tab
        if (t > IMG_SLOW_MS && !w.__cbWasHidden) report('cb_img_slow', shortUrl(last.url || last.element.currentSrc), t);
      }).observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) { /* LCP not supported (older iOS) */ }
  }
  d.addEventListener('visibilitychange', function () {
    if (d.visibilityState === 'hidden') w.__cbWasHidden = true;
  });
  if (d.visibilityState === 'hidden') w.__cbWasHidden = true;

  /* ---------- 3. Add to cart ---------- */
  /*
   * TRACKING-FIX (13 Oct 2026, Claude) - cb_atc_no_response over-counted and missed hangs.
   * What: (1) a repeat tap while an add was still in progress (the theme ignores those by design)
   *       was counted as "nothing happened" even when the add succeeded - 33 of the 70 users with a
   *       main-button no_response had already added to cart; (2) a sticky-bar tap was counted twice
   *       (sticky + the main-button click it forwards); (3) a request that never answered was not
   *       reported at all (only errors and slow answers were); (4) sticky taps on a sold-out choice
   *       were mixed in with real failures. Evidence: GA4 25 Sep-13 Oct 2026, live tests 13 Oct.
   * Change: count requests in flight and skip taps made during one; skip the forwarded click;
   *       new cb_atc_hang and cb_atc_soldout_tap events. Event names, parameters and the GA4 /
   *       Clarity destinations are unchanged, so existing reports keep working.
   * Undo: restore this section from the 13Oct - 2pm theme (id 166636978392).
   */
  var lastAtcRequest = 0;
  var atcInFlight = 0;
  var lastStickyTap = 0;
  // Called when a cart request starts; returns the function to call once it settles.
  function atcStarted() {
    atcInFlight++;
    var settled = false;
    var hang = setTimeout(function () {
      if (!settled && d.visibilityState === 'visible') report('cb_atc_hang', 'no answer', ATC_HANG_MS);
    }, ATC_HANG_MS);
    return function () {
      if (settled) return;
      settled = true;
      clearTimeout(hang);
      atcInFlight = Math.max(0, atcInFlight - 1);
    };
  }
  function isAtcUrl(u) { return /\/cart\/add(\.js)?(\?|$)/.test(String(u || '')); }
  function atcResult(status, ms, text) {
    if (status >= 200 && status < 300) {
      if (ms > ATC_SLOW_MS) report('cb_atc_slow', 'status ' + status, ms);
      return;
    }
    var msg = '';
    try { var j = JSON.parse(text); msg = j.description || j.message || ''; } catch (e) { msg = (text || '').slice(0, 120); }
    report('cb_atc_fail', status + ' ' + msg, ms);
  }
  try {
    var origFetch = w.fetch;
    if (origFetch) {
      w.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url);
        var p = origFetch.apply(this, arguments);
        if (!isAtcUrl(url)) return p;
        var t0 = now(); lastAtcRequest = Date.now();
        var settle = atcStarted();
        p.then(function (r) {
          settle();
          var ms = now() - t0;
          if (r.ok) { atcResult(r.status, ms); return; }
          r.clone().text().then(function (txt) { atcResult(r.status, ms, txt); }, function () { atcResult(r.status, ms, ''); });
        }, function (err) { settle(); report('cb_atc_fail', 'network ' + (err && err.message || ''), now() - t0); });
        return p;
      };
    }
  } catch (e) {}
  try {
    var xo = XMLHttpRequest.prototype.open, xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) { this.__cbAtc = isAtcUrl(u); return xo.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      if (this.__cbAtc) {
        var x = this, t0 = now(); lastAtcRequest = Date.now();
        var settle = atcStarted();
        x.addEventListener('loadend', function () {
          settle();
          if (x.status === 0) report('cb_atc_fail', 'network', now() - t0);
          else atcResult(x.status, now() - t0, typeof x.responseText === 'string' ? x.responseText : '');
        });
      }
      return xs.apply(this, arguments);
    };
  } catch (e) {}

  // The main Add to Cart button the sticky bar stands in for (same lookup as snippets/cta-sticky.liquid).
  function stickyMain(stickyBtn) {
    var form = d.getElementById(stickyBtn.getAttribute('data-form-id') || '');
    return (form && form.querySelector('.add-to-cart')) || d.querySelector('form[action*="/cart/add"] button[data-add-to-cart]');
  }
  function chosenOptions(main) {
    var scope = main.closest('[data-section-type="product"]') || d;
    var picked = [].map.call(scope.querySelectorAll('.variant-wrapper input:checked'), function (i) { return i.value; });
    return picked.length ? picked.join(' / ') : 'variant ' + ((main.form && main.form.querySelector('[name="id"]') || {}).value || '?');
  }

  // Tap on Add to Cart that never produced a cart request (JS error, stuck button, etc.)
  d.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('form[action*="/cart/add"] button[name="add"], form[action*="/cart/add"] [type="submit"], .sticky-add-to-cart');
    if (!btn || btn.disabled) return;
    var tapAt = Date.now(), which = btn.classList.contains('sticky-add-to-cart') ? 'sticky' : 'main';
    if (which === 'main' && !e.isTrusted && tapAt - lastStickyTap < 500) return; // forwarded by the sticky bar, already watched
    if (which === 'sticky') {
      lastStickyTap = tapAt;
      var main = stickyMain(btn);
      if (main && main.disabled) { report('cb_atc_soldout_tap', chosenOptions(main)); return; }
    }
    if (atcInFlight > 0) return; // repeat tap while an add is in progress - ignored by the theme on purpose
    setTimeout(function () {
      if (lastAtcRequest < tapAt && d.visibilityState === 'visible' && !btn.disabled) {
        report('cb_atc_no_response', which + ' button');
      }
    }, ATC_NO_REQUEST_MS);
  }, true);

  /* ---------- 1/checkout. Razorpay Magic Checkout ---------- */
  var co = null; // current checkout attempt
  function checkoutVisible() { return !!d.querySelector('.razorpay-container-visible'); }
  function markOpen() {
    if (!co || co.opened) return;
    co.opened = true;
    var ms = Date.now() - co.at;
    report('cb_checkout_open', co.from, ms);
    if (ms > CHECKOUT_SLOW_MS) report('cb_checkout_slow', co.from, ms);
  }
  d.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[name="checkout"]');
    if (!btn) return;
    co = { at: Date.now(), from: btn.closest('.drawer, #CartDrawer') ? 'cart_drawer' : 'cart', opened: false, steps: [], leaving: false };
    var attempt = co;
    report('cb_checkout_click', co.from + (typeof w.Razorpay === 'function' ? '' : ' razorpay_not_loaded'));
    setTimeout(function () {
      if (attempt !== co || attempt.opened || attempt.leaving) return;
      if (checkoutVisible()) { markOpen(); return; }
      if (d.visibilityState !== 'visible') return; // app switched / backgrounded
      report('cb_checkout_no_open', attempt.from + (typeof w.Razorpay === 'function' ? '' : ' razorpay_not_loaded'), CHECKOUT_FAIL_MS);
    }, CHECKOUT_FAIL_MS);
  }, true);
  w.addEventListener('pagehide', function () { if (co) co.leaving = true; });
  w.addEventListener('beforeunload', function () { if (co) co.leaving = true; });

  try {
    new MutationObserver(function () { if (co && !co.opened && checkoutVisible()) markOpen(); })
      .observe(d.body || d.documentElement, { attributes: true, subtree: true, attributeFilter: ['class'] });
  } catch (e) {}

  // Razorpay's checkout iframe posts its progress to the page; read (never modify) it.
  w.addEventListener('message', function (e) {
    if (!/^https:\/\/([a-z0-9-]+\.)?razorpay\.com$/.test(e.origin)) return;
    var m = e.data;
    try { if (typeof m === 'string') m = JSON.parse(m); } catch (x) { return; }
    if (!m || m.source !== 'frame') return;
    if (m.event === 'render') markOpen();
    var inner = m.data && m.data.event;
    if (m.event === 'event' && inner === 'mx-analytics' && co) {
      var step = m.data.data && m.data.data.event;
      if (step && co.steps[co.steps.length - 1] !== step) co.steps.push(step);
    }
    if ((m.event === 'dismiss' || (m.event === 'merchantevent' && inner === 'checkout.close')) && co && co.opened && !co.closed) {
      co.closed = true;
      var last = co.steps.filter(function (s) { return s !== 'checkout_abandoned'; }).pop() || 'summary';
      if (/success|purchase|placed|complete/i.test(last)) return;
      w.clarity('set', 'cb_checkout_exit_step', last);
      w.clarity('set', 'cb_checkout_steps', co.steps.join('>').slice(0, 250));
      report('cb_checkout_closed', last, Date.now() - co.at);
    }
  });
})();
